import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  UserCheck,
  UserX,
  Mail,
  Shield,
  Clock,
  MoreVertical,
  X,
  Loader2,
  RefreshCw,
  Key,
  Activity,
  Download,
  Eye,
  History,
  ShieldCheck,
  ShieldAlert,
  UserCog,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatDate } from '@/lib/utils';
import api from '@/services/api';
import { getErrorMessage, isPermissionError } from '@/lib/errorHandler';
import { PermissionGate } from '@/components/PermissionGate';

interface User {
  id: string;
  email: string;
  username?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'SUPERVISOR' | 'TECHNICIAN' | 'STAFF' | 'VIEWER' | 'USER';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  isActive?: boolean;
  lastLogin: string | null;
  createdAt: string;
  branch?: { id: string; name: string };
  department?: { id: string; name: string };
}

interface UserStats {
  total: number;
  active: number;
  inactive: number;
  byRole: Record<string, number>;
}

interface UserFormData {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  role: User['role'];
  phone?: string;
  branchId?: string;
  departmentId?: string;
}

const roleColors: Record<string, string> = {
  SUPER_ADMIN: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white',
  ADMIN: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  MANAGER: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  SUPERVISOR: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  TECHNICIAN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  STAFF: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  VIEWER: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-400',
  USER: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

const roleIcons: Record<string, typeof Shield> = {
  SUPER_ADMIN: ShieldCheck,
  ADMIN: ShieldAlert,
  MANAGER: UserCog,
  SUPERVISOR: Users,
  TECHNICIAN: Activity,
  STAFF: Users,
  VIEWER: Eye,
  USER: Users,
};

const roleDescriptions: Record<string, string> = {
  SUPER_ADMIN: 'Full system access, can manage all settings',
  ADMIN: 'Administrative access, can manage users and settings',
  MANAGER: 'Can manage teams and approve requests',
  SUPERVISOR: 'Can oversee technicians and operations',
  TECHNICIAN: 'Can handle tickets and maintenance',
  STAFF: 'Basic access to assigned resources',
  VIEWER: 'Read-only access',
  USER: 'Standard user access',
};

const statusColors: Record<User['status'], string> = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  INACTIVE: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  SUSPENDED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [passwordResetUser, setPasswordResetUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showBulkRoleModal, setShowBulkRoleModal] = useState(false);
  const [bulkRole, setBulkRole] = useState<User['role']>('STAFF');
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    password: '',
    role: 'USER',
  });

  // Fetch users
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['users', search, roleFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch user stats
  const { data: statsData } = useQuery({
    queryKey: ['users-stats'],
    queryFn: async () => {
      const response = await api.get('/users/stats');
      const data = response.data.data;
      // Transform byRole array to object
      const byRole: Record<string, number> = {};
      if (Array.isArray(data.byRole)) {
        data.byRole.forEach((item: { role: string; count: number }) => {
          byRole[item.role] = item.count;
        });
      }
      return { ...data, byRole } as UserStats;
    },
  });

  // Fetch user activity when details modal is open
  const { data: userActivityData } = useQuery({
    queryKey: ['user-activity', selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser) return null;
      const response = await api.get(`/activity-logs?entityType=User&entityId=${selectedUser.id}&limit=10`);
      return response.data;
    },
    enabled: !!selectedUser && showDetailsModal,
  });

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const response = await api.post('/users', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-stats'] });
      closeModal();
    },
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UserFormData> }) => {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-stats'] });
      closeModal();
    },
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-stats'] });
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.put(`/users/${id}`, { isActive });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-stats'] });
    },
  });

  // Password reset mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ id, newPassword }: { id: string; newPassword: string }) => {
      const response = await api.post(`/users/${id}/change-password`, { newPassword });
      return response.data;
    },
    onSuccess: () => {
      setPasswordResetUser(null);
      setNewPassword('');
      alert('Password changed successfully!');
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to reset password');
    },
  });

  // Bulk status change mutation
  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, isActive }: { ids: string[]; isActive: boolean }) => {
      const results = await Promise.all(
        ids.map(id => api.put(`/users/${id}`, { isActive }))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-stats'] });
      setSelectedUsers(new Set());
      alert(`${selectedUsers.size} users ${bulkStatusMutation.variables?.isActive ? 'activated' : 'deactivated'} successfully!`);
    },
  });

  // Bulk role change mutation
  const bulkRoleMutation = useMutation({
    mutationFn: async ({ ids, role }: { ids: string[]; role: User['role'] }) => {
      const results = await Promise.all(
        ids.map(id => api.put(`/users/${id}`, { role }))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-stats'] });
      setSelectedUsers(new Set());
      setShowBulkRoleModal(false);
      alert(`${selectedUsers.size} users updated to ${bulkRole} role!`);
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.all(
        ids.map(id => api.delete(`/users/${id}`))
      );
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users-stats'] });
      setSelectedUsers(new Set());
      alert('Users deleted successfully!');
    },
  });

  const users: User[] = data?.data || [];
  const stats = statsData || { total: users.length, active: 0, inactive: 0, byRole: {} };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.id)));
    }
  };

  const toggleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkActivate = () => {
    if (confirm(`Activate ${selectedUsers.size} users?`)) {
      bulkStatusMutation.mutate({ ids: Array.from(selectedUsers), isActive: true });
    }
  };

  const handleBulkDeactivate = () => {
    if (confirm(`Deactivate ${selectedUsers.size} users?`)) {
      bulkStatusMutation.mutate({ ids: Array.from(selectedUsers), isActive: false });
    }
  };

  const handleBulkDelete = () => {
    if (confirm(`Delete ${selectedUsers.size} users? This action cannot be undone.`)) {
      bulkDeleteMutation.mutate(Array.from(selectedUsers));
    }
  };

  // Export users
  const handleExport = () => {
    const csvContent = [
      ['Name', 'Email', 'Role', 'Status', 'Last Login', 'Created'],
      ...users.map(u => [
        `${u.firstName} ${u.lastName}`,
        u.email,
        u.role,
        u.status || (u.isActive ? 'ACTIVE' : 'INACTIVE'),
        u.lastLogin || 'Never',
        u.createdAt,
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const openModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        username: user.username || '',
        firstName: user.firstName,
        lastName: user.lastName,
        password: '',
        role: user.role,
        phone: user.phone,
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        username: '',
        firstName: '',
        lastName: '',
        password: '',
        role: 'USER',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      username: '',
      firstName: '',
      lastName: '',
      password: '',
      role: 'USER',
    });
  };

  const openDetailsModal = (user: User) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setActionMenuOpen(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      const updateData: Partial<UserFormData> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      updateMutation.mutate({ id: editingUser.id, data: updateData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (user: User) => {
    if (confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
      deleteMutation.mutate(user.id);
    }
    setActionMenuOpen(null);
  };

  const handleToggleStatus = (user: User) => {
    const newStatus = !(user.status === 'ACTIVE' || user.isActive);
    toggleStatusMutation.mutate({ id: user.id, isActive: newStatus });
    setActionMenuOpen(null);
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-8 h-8 text-primary" />
            User Management
          </h1>
          <p className="text-muted-foreground">
            Manage system users, roles, and permissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className={cn('w-4 h-4 mr-2', isLoading && 'animate-spin')} />
            Refresh
          </Button>
          <PermissionGate permission="users.create">
            <Button onClick={() => openModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active</p>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive</p>
                <p className="text-3xl font-bold text-gray-500">{stats.inactive}</p>
              </div>
              <div className="p-3 bg-gray-100 dark:bg-gray-900/30 rounded-full">
                <XCircle className="w-6 h-6 text-gray-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Admins</p>
                <p className="text-3xl font-bold text-red-600">{(stats.byRole?.ADMIN || 0) + (stats.byRole?.SUPER_ADMIN || 0)}</p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <ShieldCheck className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[140px]"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="ADMIN">Admin</option>
                <option value="MANAGER">Manager</option>
                <option value="SUPERVISOR">Supervisor</option>
                <option value="TECHNICIAN">Technician</option>
                <option value="STAFF">Staff</option>
                <option value="VIEWER">Viewer</option>
              </select>
              <select
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[120px]"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Users ({users.length})</CardTitle>
          {selectedUsers.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{selectedUsers.size} selected</span>
              <PermissionGate permission="users.update">
                <Button size="sm" variant="outline" onClick={handleBulkActivate} disabled={bulkStatusMutation.isPending}>
                  <UserCheck className="w-4 h-4 mr-1" />
                  Activate
                </Button>
                <Button size="sm" variant="outline" onClick={handleBulkDeactivate} disabled={bulkStatusMutation.isPending}>
                  <UserX className="w-4 h-4 mr-1" />
                  Deactivate
                </Button>
              </PermissionGate>
              <PermissionGate permission="users.change_role">
                <Button size="sm" variant="outline" onClick={() => setShowBulkRoleModal(true)}>
                  <Shield className="w-4 h-4 mr-1" />
                  Change Role
                </Button>
              </PermissionGate>
              <PermissionGate permission="users.delete">
                <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleteMutation.isPending}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </PermissionGate>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive opacity-70" />
              <p className="text-destructive font-medium mb-2">
                {isPermissionError(error) 
                  ? 'You do not have access to this page. Please contact your administrator.'
                  : getErrorMessage(error)
                }
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No users found</p>
              <PermissionGate permission="users.create">
                <Button variant="link" onClick={() => openModal()}>
                  Add your first user
                </Button>
              </PermissionGate>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium w-12">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === users.length && users.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="text-left py-3 px-4 font-medium">User</th>
                    <th className="text-left py-3 px-4 font-medium">Email</th>
                    <th className="text-left py-3 px-4 font-medium">Role</th>
                    <th className="text-left py-3 px-4 font-medium">Status</th>
                    <th className="text-left py-3 px-4 font-medium">Last Login</th>
                    <th className="text-right py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className={cn("border-b hover:bg-muted/50", selectedUsers.has(user.id) && "bg-primary/5")}>
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={() => toggleSelectUser(user.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">
                              {user.firstName[0]}{user.lastName[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-muted-foreground">
                              Created {formatDate(user.createdAt)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span>{user.email}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {(() => {
                          const RoleIcon = roleIcons[user.role] || Shield;
                          return (
                            <span className={cn('px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 w-fit', roleColors[user.role])}>
                              <RoleIcon className="w-3 h-3" />
                              {user.role}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={cn('px-2 py-1 text-xs font-medium rounded-full', user.status === 'ACTIVE' || user.isActive ? statusColors.ACTIVE : statusColors.INACTIVE)}>
                          {user.status || (user.isActive ? 'ACTIVE' : 'INACTIVE')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2 relative">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                          {actionMenuOpen === user.id && (
                            <div className="absolute right-0 top-full mt-1 w-56 bg-popover border rounded-lg shadow-lg z-10">
                              <div className="p-2 border-b">
                                <p className="text-xs text-muted-foreground px-2">Quick Actions</p>
                              </div>
                              <button
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted text-left"
                                onClick={() => openDetailsModal(user)}
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              <PermissionGate permission="users.update">
                                <button
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted text-left"
                                  onClick={() => {
                                    openModal(user);
                                    setActionMenuOpen(null);
                                  }}
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Edit User
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="users.reset_password">
                                <button
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted text-left"
                                  onClick={() => {
                                    setPasswordResetUser(user);
                                    setActionMenuOpen(null);
                                  }}
                                >
                                  <Key className="w-4 h-4" />
                                  Reset Password
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="users.update">
                                <button
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted text-left"
                                  onClick={() => handleToggleStatus(user)}
                                >
                                  {(user.status === 'ACTIVE' || user.isActive) ? (
                                    <>
                                      <UserX className="w-4 h-4" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4" />
                                      Activate
                                    </>
                                  )}
                                </button>
                              </PermissionGate>
                              <hr className="border-border my-1" />
                              <PermissionGate permission="users.delete">
                                <button
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-muted text-left text-destructive"
                                  onClick={() => handleDelete(user)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete User
                                </button>
                              </PermissionGate>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
              <Button variant="ghost" size="icon" onClick={closeModal}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid gap-4 grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  {editingUser ? 'New Password (leave blank to keep current)' : 'Password'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                >
                  <option value="VIEWER">Viewer - Read-only access</option>
                  <option value="STAFF">Staff - Basic access</option>
                  <option value="TECHNICIAN">Technician - Handle tickets & maintenance</option>
                  <option value="SUPERVISOR">Supervisor - Oversee operations</option>
                  <option value="MANAGER">Manager - Team management</option>
                  <option value="ADMIN">Admin - Administrative access</option>
                  <option value="SUPER_ADMIN">Super Admin - Full system access</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {roleDescriptions[formData.role]}
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActionMenuOpen(null)}
        />
      )}

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-background">
              <h2 className="text-lg font-semibold">User Details</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowDetailsModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6">
              {/* User Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-2xl font-bold">
                  {selectedUser.firstName[0]}{selectedUser.lastName[0]}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold">{selectedUser.firstName} {selectedUser.lastName}</h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={cn('px-3 py-1 text-xs font-medium rounded-full', roleColors[selectedUser.role] || roleColors.USER)}>
                      {selectedUser.role}
                    </span>
                    <span className={cn('px-3 py-1 text-xs font-medium rounded-full', 
                      (selectedUser.status === 'ACTIVE' || selectedUser.isActive) ? statusColors.ACTIVE : statusColors.INACTIVE
                    )}>
                      {selectedUser.status || (selectedUser.isActive ? 'ACTIVE' : 'INACTIVE')}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => {
                    setShowDetailsModal(false);
                    openModal(selectedUser);
                  }}>
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>

              {/* User Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">Email</span>
                    </div>
                    <p className="font-medium">{selectedUser.email}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Shield className="w-4 h-4" />
                      <span className="text-sm">Role</span>
                    </div>
                    <p className="font-medium">{selectedUser.role}</p>
                    <p className="text-xs text-muted-foreground">{roleDescriptions[selectedUser.role]}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">Last Login</span>
                    </div>
                    <p className="font-medium">{selectedUser.lastLogin ? formatDate(selectedUser.lastLogin) : 'Never'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">Created</span>
                    </div>
                    <p className="font-medium">{formatDate(selectedUser.createdAt)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Role Permissions */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5" />
                    Role Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedUser.role === 'SUPER_ADMIN' && (
                      <>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Full system access</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Manage all users</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> System configuration</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> View audit logs</div>
                      </>
                    )}
                    {selectedUser.role === 'ADMIN' && (
                      <>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Manage users</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Manage assets</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> View reports</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Manage settings</div>
                      </>
                    )}
                    {selectedUser.role === 'MANAGER' && (
                      <>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Manage team</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Approve requests</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> View reports</div>
                      </>
                    )}
                    {selectedUser.role === 'SUPERVISOR' && (
                      <>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Oversee technicians</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Approve maintenance</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> View team reports</div>
                      </>
                    )}
                    {selectedUser.role === 'TECHNICIAN' && (
                      <>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Handle tickets</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Perform maintenance</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Update asset status</div>
                      </>
                    )}
                    {(selectedUser.role === 'STAFF' || selectedUser.role === 'USER') && (
                      <>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> View assigned assets</div>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Create tickets</div>
                      </>
                    )}
                    {selectedUser.role === 'VIEWER' && (
                      <>
                        <div className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500" /> Read-only access</div>
                        <div className="flex items-center gap-2 text-sm"><XCircle className="w-4 h-4 text-red-500" /> Cannot modify data</div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {userActivityData?.data?.length > 0 ? (
                    <div className="space-y-3">
                      {userActivityData.data.slice(0, 5).map((activity: any) => (
                        <div key={activity.id} className="flex items-start gap-3 text-sm">
                          <div className="w-2 h-2 mt-2 rounded-full bg-primary" />
                          <div className="flex-1">
                            <p className="font-medium">{activity.description}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(activity.createdAt)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No recent activity</p>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => {
                  setPasswordResetUser(selectedUser);
                  setShowDetailsModal(false);
                }}>
                  <Key className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    handleToggleStatus(selectedUser);
                    setShowDetailsModal(false);
                  }}
                >
                  {(selectedUser.status === 'ACTIVE' || selectedUser.isActive) ? (
                    <>
                      <UserX className="w-4 h-4 mr-2" />
                      Deactivate User
                    </>
                  ) : (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Activate User
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {passwordResetUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">Reset Password</h2>
              <Button variant="ghost" size="icon" onClick={() => {
                setPasswordResetUser(null);
                setNewPassword('');
              }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {passwordResetUser.firstName[0]}{passwordResetUser.lastName[0]}
                  </span>
                </div>
                <div>
                  <p className="font-medium">{passwordResetUser.firstName} {passwordResetUser.lastName}</p>
                  <p className="text-sm text-muted-foreground">{passwordResetUser.email}</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Password must be at least 8 characters
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => {
                  setPasswordResetUser(null);
                  setNewPassword('');
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => resetPasswordMutation.mutate({ id: passwordResetUser.id, newPassword })}
                  disabled={!newPassword || newPassword.length < 8 || resetPasswordMutation.isPending}
                >
                  {resetPasswordMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Reset Password
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Role Change Modal */}
      {showBulkRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">Change Role for {selectedUsers.size} Users</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowBulkRoleModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bulkRole">New Role</Label>
                <select
                  id="bulkRole"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={bulkRole}
                  onChange={(e) => setBulkRole(e.target.value as User['role'])}
                >
                  <option value="VIEWER">Viewer - Read-only access</option>
                  <option value="STAFF">Staff - Basic access</option>
                  <option value="TECHNICIAN">Technician - Handle tickets & maintenance</option>
                  <option value="SUPERVISOR">Supervisor - Oversee operations</option>
                  <option value="MANAGER">Manager - Team management</option>
                  <option value="ADMIN">Admin - Administrative access</option>
                  <option value="SUPER_ADMIN">Super Admin - Full system access</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  {roleDescriptions[bulkRole]}
                </p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  ⚠️ This will change the role for {selectedUsers.size} user(s). This action will be logged.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowBulkRoleModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => bulkRoleMutation.mutate({ ids: Array.from(selectedUsers), role: bulkRole })}
                  disabled={bulkRoleMutation.isPending}
                >
                  {bulkRoleMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Change Role
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
